import React, { useState, useEffect } from "react";
import styles from "./Contact.module.css";
import { openPrivacyPolicy } from "../../utils/cookieEvents";
import emailjs from "@emailjs/browser";

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: false,
    message: "",
    privacy: false
  });

  const [errors, setErrors] = useState({});
  const [hideToast, setHideToast] = useState({
    name: false,
    email: false,
    phone: false,
    service: false,
    message: false
  });

  // 🔍 Funkcja walidująca pojedyncze pole
  const validateField = (name, value, type) => {
    let error = "";

    const nameRegex =
      /^[A-Za-zĄąĆćĘęŁłŃńÓóŚśŹźŻż]{2,15}\s[A-Za-zĄąĆćĘęŁłŃńÓóŚśŹźŻż]{3,32}(?:[-\s][A-Za-zĄąĆćĘęŁłŃńÓóŚśŹźŻż]{3,32})?$/;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const phoneRegex = /^(\+48)?\s?(\d{3}[\s-]?){2}\d{3}$/;
    const messageRegex =
      /^[A-Za-z0-9ĄąĆćĘęŁłŃńÓóŚśŹźŻż.,!?"'()\-_\s]{10,1000}$/;

    switch (name) {
      case "name":
        if (!value.trim()) {
          error = "Podaj imię i nazwisko.";
        } else if (!nameRegex.test(value)) {
          error = "Wpisz imię i nazwisko w prawidłowym formacie.";
        }
        break;

      case "email":
        if (!value.trim()) {
          error = "Podaj adres e-mail.";
        } else if (!emailRegex.test(value)) {
          error = "Nieprawidłowy format adresu e-mail.";
        }
        break;

      case "phone":
        if (!value.trim()) {
          error = "Podaj numer telefonu.";
        } else if (!phoneRegex.test(value)) {
          error = "Wpisz numer telefonu w prawidłowym formacie.";
        }
        break;

      case "service":
        if (!value || value === "") {
          error = "Proszę wybrać rodzaj usługi";
        }
        break;
      case "message":
        if (!value.trim()) {
          error = "Wpisz treść wiadomości.";
        } else if (!messageRegex.test(value)) {
          error = "Wpisz treść wiadomości w prawidłowym formacie.";
        }
        break;

      default:
        break;
    }

    return error;
  };

  const [banner, setBanner] = useState({
    show: false,
    message: "",
    type: "" // "success" | "error"
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    // aktualizujemy dane
    setFormData((prev) => ({ ...prev, [name]: newValue }));

    // walidujemy konkretne pole
    const fieldError = validateField(name, newValue, type);

    // aktualizujemy błędy – tylko dla danego pola
    setErrors((prev) => ({ ...prev, [name]: fieldError }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const fieldError = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: fieldError }));
  };

  useEffect(() => {
    const timers = {};

    Object.keys(errors).forEach((field) => {
      const value = formData[field];
      const error = errors[field];

      if (!error) {
        // brak błędu → dymek nie pokazuje się
        setHideToast((prev) => ({ ...prev, [field]: false }));
        return;
      }

      if (typeof value === "string" && value.trim() === "") {
        // błąd + pole puste → pokazujemy dymek chwilowo
        setHideToast((prev) => ({ ...prev, [field]: false }));

        timers[field] = setTimeout(() => {
          setHideToast((prev) => ({ ...prev, [field]: true }));
        }, 15000);
      } else {
        // błąd + treść błędna → dymek zostaje
        setHideToast((prev) => ({ ...prev, [field]: false }));
      }
    });

    return () => Object.values(timers).forEach(clearTimeout);
  }, [errors, formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Walidacja wszystkich pól
    const newErrors = {};
    Object.entries(formData).forEach(([key, value]) => {
      const fieldError = validateField(key, value);
      if (fieldError) newErrors[key] = fieldError;
    });
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    // 🔹 Konwersja checkboxa privacy na "TAK"/"NIE"
    const emailData = {
      ...formData,
      privacy: formData.privacy ? "TAK" : "NIE"
    };

    try {
      const result = await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID_WEB,
        emailData,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      ); // używamy emailData zamiast formData

      if (result.status === 200) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "contact_form_submit"
        });

        setFormData({
          name: "",
          email: "",
          phone: "",
          service: "", //dodane
          message: "",
          privacy: false
        });
        setErrors({});
        setBanner({
          show: true,
          message: "Wiadomość wysłana!",
          type: "success"
        });
        setTimeout(
          () =>
            setBanner({
              show: false,
              message: "",
              type: ""
            }),
          4000
        );
      } else {
        setBanner({
          show: true,
          message: "Wysłanie wiadomości nie powiodło się.",
          type: "error"
        });
        setTimeout(
          () =>
            setBanner({
              show: false,
              message: "",
              type: ""
            }),
          2000
        );
      }
    } catch (error) {
      console.error("Błąd wysyłki:", error);
      setBanner({
        show: true,
        message: "Błąd wysyłki. Spróbuj ponownie.",
        type: "error"
      });
      setTimeout(
        () =>
          setBanner({
            show: false,
            message: "",
            type: ""
          }),
        4000
      );
    }
  };

  return (
    <section id="contact" className="sectionContainer dark">
      <div className="sectionTitle">
        <h2>Skontaktuj się z nami</h2>
      </div>
      <div className={styles.contactContainer}>
        <div className={styles.contactElements}>
          <div className="sectionTitle">
            <h4>Dane kontaktowe</h4>
          </div>
          <div className={styles.contactItem}>
            <div className={styles.contentText}>
              <h3>Telefon</h3>
              <p>
                <a href="tel:+48123456789" className={styles.phoneLink}>
                  +48 734 959 867
                </a>
              </p>
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-smartphone-icon lucide-smartphone"
            >
              <rect width="14" height="20" x="5" y="2" rx="2" ry="2" />
              <path d="M12 18h.01" />
            </svg>
          </div>
          <div className={styles.contactItem}>
            <div className={styles.contentText}>
              <h3>Email</h3>
              <p>biuro.artkoz@gmail.com</p>
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-mail-icon lucide-mail"
            >
              <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" />
              <rect x="2" y="4" width="20" height="16" rx="2" />
            </svg>
          </div>
          <div className={styles.contactItem}>
            <div className={styles.contentText}>
              <h3>Gdzie działamy</h3>
              <ul>
                <li>
                  Siedzibą naszej działalności są okolice Lublina, jednak
                  działamy znacznie szerzej.
                </li>
                <li>
                  Strony internetowe tworzymy dla klientów z całej Polski,
                  natomiast szkolenia prowadzimy stacjonarnie, z możliwością
                  dojazdu oraz online.
                </li>
              </ul>
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-map-pin-house-icon lucide-map-pin-house"
            >
              <path d="M15 22a1 1 0 0 1-1-1v-4a1 1 0 0 1 .445-.832l3-2a1 1 0 0 1 1.11 0l3 2A1 1 0 0 1 22 17v4a1 1 0 0 1-1 1z" />
              <path d="M18 10a8 8 0 0 0-16 0c0 4.993 5.539 10.193 7.399 11.799a1 1 0 0 0 .601.2" />
              <path d="M18 22v-3" />
              <circle cx="10" cy="10" r="3" />
            </svg>
          </div>
        </div>

        {banner.show && (
          <div
            className={`${styles.toastTop} ${banner.type === "success" ? styles.toastSuccess : styles.toastError}`}
          >
            <span className={styles.toastIcon}>
              {banner.type === "success" ? "✔️" : "❌"}
            </span>
            <span>{banner.message}</span>
          </div>
        )}

        {/* 🔹 Jeśli są błędy walidacji */}
        {/* {Object.keys(errors).length > 6 && (
          <div className={`${styles.toastTop} ${styles.toastError}`}>
            <span className={styles.toastIcon}>⚠️</span>
            <span>Uzupełnij poprawnie wszystkie wymagane pola.</span>
          </div>
        )} */}

        <form
          className={styles.contactForm}
          id="contactForm"
          onSubmit={handleSubmit}
        >
          <div className={styles.sectionTitleForm}>
            <h3>Napisz do nas</h3>
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="name" className={styles.labels}>
              Imię i nazwisko *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              className={styles.formItem}
              value={
                formData.name // required
              }
              onChange={handleChange}
            />
            {/* <span className={styles.errorMessage} /> */}
            {errors.name && !hideToast.name && (
              <div className={styles.fieldToast}>
                <span className={styles.fieldToastIcon}>⚠️</span>
                {errors.name}
              </div>
            )}
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="email" className={styles.labels}>
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className={styles.formItem}
              value={
                formData.email // required
              }
              onChange={handleChange}
            />
            {/* <span className={styles.errorMessage} /> */}
            {errors.email && !hideToast.email && (
              <div className={styles.fieldToast}>
                <span className={styles.fieldToastIcon}>⚠️</span>
                {errors.email}
              </div>
            )}
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="phone" className={styles.labels}>
              Telefon *
            </label>
            <input
              type="phone"
              id="phone"
              name="phone"
              className={styles.formItem}
              value={
                formData.phone // required
              }
              onChange={handleChange}
            />
            {/* <span className={styles.errorMessage} /> */}
            {errors.phone && !hideToast.phone && (
              <div className={styles.fieldToast}>
                <span className={styles.fieldToastIcon}>⚠️</span>
                {errors.phone}
              </div>
            )}
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="service" className={styles.labels}>
              Rodzaj usługi *
            </label>

            <div className={styles.customSelectWrapper}>
              <select
                id="service"
                name="service"
                className={styles.customSelect}
                // value={formData.service || ""}
                value={formData.service ?? ""}
                onChange={(e) => handleChange(e)}
                required
                onBlur={handleBlur}
              >
                <option value="">Wybierz rodzaj usługi</option>
                <option value="Strona WWW">Strona WWW</option>
                <option value="Szkolenie">Szkolenie</option>
                <option value="Współpraca">Współpraca</option>
                <option value="Inne">Inne</option>
              </select>
            </div>

            {errors.service && (
              <div className={styles.fieldToast}>
                <span className={styles.fieldToastIcon}>⚠️</span>
                {errors.service}
              </div>
            )}
            {/* {errors.service && (
              <span className={styles.error}>{errors.service}</span>
            )} */}
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="message" className={styles.labels}>
              Wiadomość *
            </label>
            <textarea
              type="text"
              id="message"
              name="message"
              className={`${styles.formItem} ${styles.message}`}
              rows="5"
              value={
                formData.message // required
              }
              onChange={handleChange}
            />
            {/* <span className={styles.errorMessage} /> */}
            {errors.message && !hideToast.message && (
              <div className={styles.fieldToast}>
                <span className={styles.fieldToastIcon}>⚠️</span>
                {errors.message}
              </div>
            )}
          </div>
          <div className={styles.formGroup}>
            <p className={styles.privacyInfo}>
              Administratorem Państwa danych jest firma <strong>ArtKoz</strong>.
              Dane przetwarzane są w celu obsługi Państwa zapytania. Więcej
              informacji znajdziesz w&nbsp;
              <button
                id="policyPrivacy"
                type="button"
                className={styles.btnChangeSettings}
                onClick={openPrivacyPolicy}
              >
                Polityka prywatności
              </button>
            </p>
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="privacy" className={styles.labelsPrivacy}>
              <input
                type="checkbox"
                id="privacy"
                name="privacy"
                checked={formData.privacy}
                onChange={handleChange}
                className={styles.privacy}
              />
              <span className={styles.customCheckbox} />
              Wyrażam zgodę na przetwarzanie moich danych osobowych przez firmę{" "}
              <strong> ArtKoz </strong> w celu udzielenia odpowiedzi na
              przesłane zapytanie oraz przedstawienia oferty handlowej, zgodnie
              z&nbsp;
              <button
                id="policyPrivacyCheck"
                type="button"
                className={styles.btnChangeSettings}
                onClick={openPrivacyPolicy}
              >
                Polityka prywatności
              </button>
            </label>
            {/* <span className={styles.errorMessage} /> */}
          </div>
          <div className={styles.buttonElementInForm}>
            <button type="submit" className={styles.formButton}>
              Wyślij
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};
export default ContactForm;
